import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Modal, Field, inputCls, PrimaryBtn, GhostBtn, DangerBtn, DataTable } from "../components/DataTable";
import { toast } from "sonner";

const empty = { email: "", password: "", name: "", role: "client", site_ids: [] };

const ROLE_LABEL = { super_admin: "SUPER ADMIN", plant_admin: "PLANT ADMIN", client: "CLIENT", admin: "SUPER ADMIN" };

export default function Users() {
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/users").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    api.get("/sites").then(({ data }) => setSites(data));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, site_ids: form.role === "super_admin" ? [] : (form.site_ids || []) };
      if (editId) {
        const upd = { name: payload.name, role: payload.role, site_ids: payload.site_ids };
        if (payload.password) upd.password = payload.password;
        await api.put(`/users/${editId}`, upd);
      } else {
        await api.post("/auth/register", payload);
      }
      toast.success(editId ? "User updated" : "User created");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete user?")) return;
    try { await api.delete(`/users/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleSite = (sid) => {
    setForm((f) => ({
      ...f,
      site_ids: f.site_ids?.includes(sid) ? f.site_ids.filter(x => x !== sid) : [...(f.site_ids || []), sid],
    }));
  };

  const siteNames = (ids) => (ids || []).map(id => sites.find(s => s.id === id)?.name).filter(Boolean).join(", ") || "—";

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="scada-label">ACCESS CONTROL</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter">Users</h1>
        </div>
        <PrimaryBtn data-testid="add-user-btn" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }}>+ Add User</PrimaryBtn>
      </div>

      <DataTable
        testId="users-table"
        columns={[
          { key: "name", label: "NAME" },
          { key: "email", label: "EMAIL" },
          { key: "role", label: "ROLE", w: "0.9fr", render: r => <span className="font-mono-metric uppercase text-[10px]" style={{ color: r.role === "super_admin" ? "#00E5FF" : r.role === "plant_admin" ? "#FFB300" : "#9BA4B5" }}>{ROLE_LABEL[r.role] || r.role}</span> },
          { key: "site_ids", label: "SITES", w: "1.6fr", render: r => <span className="text-[#9BA4B5]">{siteNames(r.site_ids)}</span> },
        ]}
        rows={rows}
        actions={(r) => (
          <>
            <GhostBtn data-testid={`edit-user-${r.id}`} onClick={() => { setForm({ ...r, password: "" }); setEditId(r.id); setOpen(true); }}>Edit</GhostBtn>
            <DangerBtn data-testid={`del-user-${r.id}`} onClick={() => remove(r.id)}>Del</DangerBtn>
          </>
        )}
      />

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit User" : "Add User"} testId="user-modal">
        <form onSubmit={save} className="max-h-[70vh] overflow-y-auto pr-2">
          <Field label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls()} data-testid="user-name" /></Field>
          {!editId && <Field label="Email"><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls()} data-testid="user-email" /></Field>}
          <Field label={editId ? "Password (leave blank to keep)" : "Password"}>
            <input required={!editId} type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls()} data-testid="user-password" />
          </Field>
          <Field label="Role">
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls()} data-testid="user-role">
              <option value="client">Client (Factory Owner)</option>
              <option value="plant_admin">Plant Admin (site config, no user mgmt)</option>
              <option value="super_admin">Super Admin (all access)</option>
            </select>
          </Field>
          {form.role !== "super_admin" && (
            <Field label="Assigned Sites (one or more)">
              <div className="scada-panel p-2 max-h-40 overflow-y-auto" data-testid="user-sites">
                {sites.length === 0 && <div className="text-[#5A6573] text-xs font-mono-metric">No sites available</div>}
                {sites.map(s => (
                  <label key={s.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-[#1E2226]/50 px-2">
                    <input type="checkbox" checked={form.site_ids?.includes(s.id) || false} onChange={() => toggleSite(s.id)} data-testid={`user-site-${s.id}`} />
                    <span className="font-mono-metric text-xs text-[#E3E8EE]">{s.name} <span className="text-[#5A6573]">— {s.customer}</span></span>
                  </label>
                ))}
              </div>
            </Field>
          )}
          <div className="flex justify-end gap-2 mt-4"><PrimaryBtn type="submit" data-testid="save-user">Save</PrimaryBtn></div>
        </form>
      </Modal>
    </div>
  );
}
